import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { AreaOfUse } from '../../../core/model';
import { AreaOfUseAdminService } from '../../../features/area-of-use-admin-base';
import { NoHashValidator } from '../../validators/no-hash-validator';

@Component({
  selector: 'py-add-to-area-of-use',
  templateUrl: './add-to-area-of-use.component.html',
  styleUrls: ['./add-to-area-of-use.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AddToAreaOfUseComponent implements OnInit, OnDestroy {
  @Input() productCode;
  @Input() showButton?: boolean = false;
  @Input() disableTooltip?: boolean = false;

  categories$: Observable<any[]>;
  loading$: Observable<boolean>;
  subCategories: AreaOfUse[];
  subscriptions = new Subscription();

  formGroup = new UntypedFormGroup({
    search: new UntypedFormControl('', [Validators.maxLength(35), NoHashValidator.noHashes]),
  });

  get filterBy() {
    return this.formGroup?.get('search')?.value;
  }

  constructor(private areaOfUseAdminService: AreaOfUseAdminService) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    this.categories$ = this.areaOfUseAdminService.getAreaOfUseCategories().pipe(
      map((categories) => {
        return categories.map((category) => {
          //Only top level categories will have the productCodes array
          const level1Categories = categories.filter((c) => c.level === 1);
          return {
            ...category,
            subCategories: category.subCategories?.map(
              (subCategory) => level1Categories.find((level1Category) => level1Category.code === subCategory.code) || subCategory
            ),
          };
        });
      }),
      map((categories) => categories?.filter((category) => category.subCategories?.length))
    );

    this.loading$ = combineLatest([
      this.areaOfUseAdminService.getLoadAreasOfUseForProductLoading(),
      this.areaOfUseAdminService.getAddProductToAreaOfUseLoading(),
      this.areaOfUseAdminService.getRemoveProductsFromAreaOfUseLoading(),
    ]).pipe(map(([productsLoading, addLoading, removeLoading]) => productsLoading || addLoading || removeLoading));

    this.subscriptions.add(
      this.areaOfUseAdminService.getAddProductToAreaOfUseSuccess().subscribe(() => {
        this.areaOfUseAdminService.addProductToAreaOfUseReset();
      })
    );

    this.subscriptions.add(
      this.areaOfUseAdminService.getRemoveProductsFromAreaOfUseSuccess().subscribe(() => {
        this.areaOfUseAdminService.removeProductsFromAreaOfUseReset();
      })
    );

    this.areaOfUseAdminService.loadAreaOfUseCategories();
    this.areaOfUseAdminService.loadAreasOfUseForProduct(this.productCode);
  }

  onResetSearchForm(): void {
    this.formGroup?.get('search')?.reset();
  }

  onSubcategoryClick(remove: boolean, categoryCode: string): void {
    if (remove) {
      this.areaOfUseAdminService.removeProductsFromAreaOfUse([this.productCode], categoryCode);
    } else {
      this.areaOfUseAdminService.addProductToAreaOfUse(this.productCode, categoryCode);
    }
  }
}
